/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app
 * 
 * Usage:
 * ```tsx
 * const { user, isLoading, logout } = useAuth()
 * 
 * if (isLoading) return <Loading />
 * if (!user) return <LoginPrompt />
 * 
 * return <Dashboard user={user} />
 * ```
 */

import { useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { User } from './api/auth'
import { getCurrentUser, logout as apiLogout, setCurrentUser } from './api/auth'
import { apiClient } from './api/client'
import { AuthContext, type AuthContextType } from './auth-context-instance'

export type { AuthContextType }

/** One session refresh per window, however many calls are refused at once. */
const FORBIDDEN_REFRESH_COOLDOWN_MS = 30_000

/**
 * How soon a FAILED refresh may be retried. Short enough that a blip costs the user one stale
 * render, long enough that a backend already answering 502 is not asked again by every refused
 * call.
 */
const FORBIDDEN_REFRESH_RETRY_MS = 3_000

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // SOLY-REGIE-006 — a 403 refreshes the permission map instead of being swallowed.
  //
  // Anti-loop, and it is load-bearing: a screen that fires three refused calls would otherwise
  // fire three `/auth/me`. The point is to notice a permission that changed under us, not to poll.
  const lastForbiddenRefreshRef = useRef(0)

  // Configure API client to handle 401 errors (BUG-05).
  //
  // Originally commented out (commit f4aebc5) because the bootstrap
  // `/auth/me` probe on public pages (/register, /verify, /reset-password)
  // kicked unauthenticated visitors to /login on mount.
  //
  // Re-enabled now that `getCurrentUser()` opts into `skipUnauthorizedHandler`
  // — the handler only fires on *other* API calls that hit 401 mid-session,
  // which is exactly when we want to force a redirect.
  useEffect(() => {
    apiClient.configure({
      onUnauthorized: () => {
        setUser(null)
        navigate('/login')
      },
      onForbidden: () => {
        // The window opens BEFORE the call, not after: the cockpit fires several requests at
        // boot and three refusals in one tick must cost ONE /auth/me. Stamping afterwards lets
        // them all through, because the mocked-fast path settles between them.
        //
        // And a refresh that FAILED reopens it. Otherwise a single network blip would buy the
        // revoked permission map a full 30 s of refusals with no retry — the exact state this
        // handler exists to escape (review 2026-09-08).
        const now = Date.now()
        if (now - lastForbiddenRefreshRef.current < FORBIDDEN_REFRESH_COOLDOWN_MS) return
        lastForbiddenRefreshRef.current = now

        // Deliberately fire-and-forget: the caller is in the middle of its own error handling and
        // must not wait on us. When the permission really is gone, the refreshed user lands in
        // state and ProtectedRoute takes the user out of the page on the next render.
        //
        // A failed refresh SHORTENS the window rather than removing it. Zeroing it disarmed the
        // anti-loop for exactly as long as /auth/me was the thing that was broken: now that the
        // probe can actually reject, every later burst of refusals bought a fresh probe against a
        // backend already answering 502 — and a request blocked with no round trip rejects fast
        // enough to reset the ref BETWEEN two responses of the same burst, turning one probe into
        // one per call.
        void refreshUser({ allowCachedFallback: false }).catch(() => {
          lastForbiddenRefreshRef.current =
            Date.now() - FORBIDDEN_REFRESH_COOLDOWN_MS + FORBIDDEN_REFRESH_RETRY_MS
        })
      },
    })

    // configure() MERGES into a module singleton and never drops a key, so without this an
    // unmounted provider leaves a live handler closed over its dead setUser. Harmless in the app
    // (AuthProvider never unmounts) — not harmless in tests, where it silently pollutes every
    // file that mounts one.
    return () => apiClient.configure({ onUnauthorized: undefined, onForbidden: undefined })
    // `refreshUser` is redeclared each render; the closure only touches setUser and the
    // module-level getCurrentUser, so the first render's copy stays correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  // Load user on mount
  useEffect(() => {
    loadUser()
  }, [])

  async function loadUser() {
    try {
      const currentUser = await getCurrentUser()
      setUser(currentUser)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function login(newUser: User) {
    setUser(newUser)
    setCurrentUser(newUser)
  }

  async function logout() {
    try {
      await apiLogout()
    } catch {
      // Ignore errors on logout
    } finally {
      setUser(null)
      navigate('/login')
    }
  }

  async function refreshUser(options: { allowCachedFallback?: boolean } = {}) {
    const currentUser = await getCurrentUser(options)
    setUser(currentUser)
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 * Use this in any component that needs auth state
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

/**
 * Hook that requires authentication
 * Automatically redirects to login if not authenticated
 * 
 * Usage:
 * ```tsx
 * function DashboardPage() {
 *   const { user } = useRequireAuth()
 *   // user is guaranteed to be non-null here
 *   return <div>Welcome {user.email}</div>
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRequireAuth() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login')
    }
  }, [user, isLoading, navigate])

  return { user, isLoading }
}
